import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCattle } from '@/contexts/CattleContext';
import { useHealth } from '@/contexts/HealthContext';
import { toast } from 'sonner';
import { Check } from 'lucide-react';

export const TreatmentDialog = ({ open, onOpenChange }) => {
  const { cattle } = useCattle();
  const { addTreatment, products, veterinarians } = useHealth();
  
  const [formData, setFormData] = useState({
    cattleId: '',
    type: '',
    product: '',
    dose: '',
    applicationDate: new Date().toISOString().split('T')[0],
    nextApplicationDate: '',
    appliedBy: '',
    indicatedBy: '',
    cost: '',
    notes: ''
  });

  const treatmentTypes = [
    'Desparasitación',
    'Vitaminación',
    'Vacunación',
    'Antibiótico',
    'Antiinflamatorio',
    'Analgésico',
    'Suplemento',
    'Otro'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.cattleId || !formData.type || !formData.product) {
      toast.error('Por favor complete los campos obligatorios');
      return;
    }

    const selectedCattle = cattle.find(c => c.id === formData.cattleId);
    addTreatment({
      ...formData,
      cost: parseFloat(formData.cost) || 0
    });
    
    toast.success('Tratamiento registrado', {
      description: `${formData.type} para ${selectedCattle?.name}`
    });
    
    // Reset and close
    setFormData({
      cattleId: '',
      type: '',
      product: '',
      dose: '',
      applicationDate: new Date().toISOString().split('T')[0],
      nextApplicationDate: '',
      appliedBy: '',
      indicatedBy: '',
      cost: '',
      notes: ''
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar Tratamiento</DialogTitle>
          <DialogDescription>
            Complete la información del tratamiento aplicado al ganado
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Animal Selection */}
          <div className="space-y-2">
            <Label>Animal *</Label>
            <Select value={formData.cattleId} onValueChange={(value) => setFormData(prev => ({ ...prev, cattleId: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccione el animal" />
              </SelectTrigger>
              <SelectContent>
                {cattle.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.tag} - {c.name} ({c.breed})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Treatment Type and Product */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo de Tratamiento *</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione tipo" />
                </SelectTrigger>
                <SelectContent>
                  {treatmentTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Producto *</Label>
              <Select value={formData.product} onValueChange={(value) => setFormData(prev => ({ ...prev, product: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione producto" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.name}>
                      {p.name} ({p.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Dose and Cost */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Dosis</Label>
              <Input
                placeholder="Ej: 10 ml"
                value={formData.dose}
                onChange={(e) => setFormData(prev => ({ ...prev, dose: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Costo ($)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.cost}
                onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Fecha de Aplicación</Label>
              <Input
                type="date"
                value={formData.applicationDate}
                onChange={(e) => setFormData(prev => ({ ...prev, applicationDate: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Próxima Aplicación</Label>
              <Input
                type="date"
                value={formData.nextApplicationDate}
                onChange={(e) => setFormData(prev => ({ ...prev, nextApplicationDate: e.target.value }))}
              />
            </div>
          </div>

          {/* Personnel */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Aplicado por</Label>
              <Select value={formData.appliedBy} onValueChange={(value) => setFormData(prev => ({ ...prev, appliedBy: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione" />
                </SelectTrigger>
                <SelectContent>
                  {veterinarians.map(v => (
                    <SelectItem key={v.id} value={v.name}>
                      {v.name} {v.role ? `(${v.role})` : v.specialty ? `(${v.specialty})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Indicado por</Label>
              <Select value={formData.indicatedBy} onValueChange={(value) => setFormData(prev => ({ ...prev, indicatedBy: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione" />
                </SelectTrigger>
                <SelectContent>
                  {veterinarians.filter(v => v.specialty || v.license).map(v => (
                    <SelectItem key={v.id} value={v.name}>
                      {v.name} {v.specialty && `(${v.specialty})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notas / Observaciones</Label>
            <Textarea
              placeholder="Detalles adicionales sobre el tratamiento..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              <Check className="mr-2 h-4 w-4" />
              Registrar Tratamiento
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
